import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const LOGO = "https://customer-assets.emergentagent.com/job_exec-hiring/artifacts/4l7749by_Ind-US-logo-png.png";

const NAV = [
  { label: 'Services', id: 'services' },
  { label: 'Industries', id: 'industries' },
  { label: 'Our Process', id: 'process' },
  { label: 'Team', id: 'team' },
  { label: 'Our Work', id: 'case-studies' },
  { label: 'Contact', id: 'contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  // Scroll state
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section via IntersectionObserver
  useEffect(() => {
    const ids = ['home', 'services', 'industries', 'process', 'team', 'case-studies', 'contact'];
    const observers = ids.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setActiveSection(id); },
        { threshold: 0.25, rootMargin: '-60px 0px -40% 0px' }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const isLight = scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-white/96 backdrop-blur-2xl border-b border-black/6 shadow-[0_1px_24px_rgba(0,0,0,0.07)]'
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[66px] flex items-center justify-between">

        {/* Logo */}
        <button
          onClick={() => scrollTo('home')}
          className="flex-shrink-0 bg-[#0A1628] px-4 py-2 rounded-sm hover:bg-[#111f38] transition-colors duration-200"
          data-testid="nav-logo"
        >
          <img src={LOGO} alt="IndUS Target" className="h-8 w-auto max-w-[140px] object-contain" />
        </button>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1">
          {NAV.map(({ label, id }) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-all duration-200 rounded-sm group ${
                  isLight
                    ? isActive ? 'text-[#E8500A]' : 'text-[#0A1628]/65 hover:text-[#0A1628]'
                    : isActive ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
                style={{ fontFamily: '"Inter", sans-serif' }}
                data-testid={`nav-${id}-link`}
              >
                {label}
                {/* Active dot */}
                <span
                  className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#E8500A] transition-all duration-200 ${
                    isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                  }`}
                />
              </button>
            );
          })}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:block">
          <button
            onClick={() => scrollTo('contact')}
            className="bg-[#E8500A] text-white text-sm font-bold px-6 py-2.5 tracking-wide rounded-sm hover:bg-[#cc4408] hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(232,80,10,0.28)] transition-all duration-200"
            style={{ fontFamily: '"Inter", sans-serif' }}
            data-testid="nav-book-call-btn"
          >
            Book a Call
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`md:hidden p-1.5 rounded-sm transition-colors ${isLight ? 'text-[#0A1628]' : 'text-white'}`}
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer — animated via max-height */}
      <div
        className={`md:hidden bg-white border-t border-black/5 overflow-hidden transition-all duration-300 ease-in-out ${
          menuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="mobile-menu"
      >
        <div className="px-6 py-4">
          {NAV.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className={`block w-full text-left py-3.5 text-sm font-medium border-b border-black/5 last:border-0 transition-colors ${
                activeSection === id ? 'text-[#E8500A]' : 'text-[#0A1628]/75 hover:text-[#0A1628]'
              }`}
              style={{ fontFamily: '"Inter", sans-serif' }}
              data-testid={`mobile-nav-${id}`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="mt-4 mb-2 w-full bg-[#E8500A] text-white py-3.5 text-sm font-bold tracking-wide rounded-sm hover:bg-[#cc4408] transition-colors"
            style={{ fontFamily: '"Inter", sans-serif' }}
            data-testid="mobile-book-call-btn"
          >
            Book a Call
          </button>
        </div>
      </div>
    </nav>
  );
}
